import {
  useEffect,
  useState,
  useMemo,
  memo
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
  Divider,
  alpha,
  Chip,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router';
import ROUTE from 'src/utils/routeConstants';
import {
  getDispenserList,
} from 'src/redux/dispenserSlice';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import EnhancedTableHead, {
  getComparator
} from 'src/common/EnhancedTableHead';
import { dispenserTableHeaders, getAlignByColumnId } from 'src/utils/tableHeaders';
import CustomPagination from 'src/common/CustomPagination';
import { getFormattedDateTime12Hour, getSelectedSiteFilterValue } from 'src/utils/getInitials';
import {
  getTableActionIconButtonSx,
  TABLE_ACTION_ICON_SX
} from 'src/common/tableActionStyles';

const formatLastSynced = (value) => {
  const formattedDateTime = getFormattedDateTime12Hour(value);
  return formattedDateTime || null;
};

const getTankName = (dispenser) => dispenser?.tank_name
  || dispenser?.tank?.name
  || dispenser?.tank?.tank_name
  || '-';

// ---------- Clean Status Pill ----------
const StatusPill = ({ status }) => (
  <Chip
    label={status ? 'Active' : 'Inactive'}
    color={status ? 'success' : 'error'}
    variant="outlined"
    size="small"
    sx={{
      fontWeight: 600
    }}
  />
);

StatusPill.propTypes = {
  status: PropTypes.bool
};

// ---------- Mobile View ----------
const MobileCardView = memo(({ dispenser, onViewClick, theme }) => {
  const lastSyncedText = formatLastSynced(dispenser.last_synced || dispenser.lastSynced);

  return (
    <Card
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Stack direction="row" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle1" fontWeight={700}>
          {dispenser.station_name || 'N/A'}
        </Typography>
        <Stack alignItems="flex-end" spacing={0.4}>
          <StatusPill status={dispenser.status} />
          {lastSyncedText && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
              Last synced:
              {' '}
              {lastSyncedText}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        {dispenser.address || '-'}
      </Typography>

      <Typography variant="body2" color="text.secondary" mt={0.5}>
        Tank:
        {' '}
        <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
          {getTankName(dispenser)}
        </Box>
      </Typography>

      <Box mt={2} textAlign="right">
        <IconButton
          size="medium"
          aria-label="View details"
          sx={getTableActionIconButtonSx(theme)}
          onClick={() => onViewClick(dispenser.id)}
        >
          <ChevronRightIcon sx={TABLE_ACTION_ICON_SX} />
        </IconButton>
      </Box>
    </Card>
  );
});

MobileCardView.displayName = 'MobileCardView';

MobileCardView.propTypes = {
  dispenser: PropTypes.object.isRequired,
  onViewClick: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired
};

// ---------- Main Component ----------
const DispenserListTable = ({
  refreshData, searchText, siteFilter, ...rest
}) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();

  const { loading, dispenserList, totalCount } = useSelector(
    (state) => state.dispensers
  );

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [limit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('station_name');
  const dispenserAlignById = useMemo(() => getAlignByColumnId(dispenserTableHeaders), []);
  const selectedSite = getSelectedSiteFilterValue(siteFilter);

  useEffect(() => {
    setPage(0);
    dispatch(
      getDispenserList({
        limit,
        pageNo: 1,
        params: { station_name: searchText, site: selectedSite }
      })
    );
  }, [dispatch, limit, refreshData, searchText, selectedSite]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(
      getDispenserList({
        limit,
        pageNo: newPage + 1,
        params: { station_name: searchText, site: selectedSite }
      })
    );
  };

  const viewDispenserInfo = (dispenserId) => {
    history.push(`${ROUTE.dispensers}/${dispenserId}`);
  };

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const displayData = dispenserList
    ?.slice()
    .sort(getComparator(order, orderBy)) || [];

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Box sx={{ height: '100%' }}>

          {/* -------- Mobile -------- */}
          {isMobile ? (
            <Box>
              {displayData.map((dispenser) => (
                <MobileCardView
                  key={dispenser.id}
                  dispenser={dispenser}
                  onViewClick={viewDispenserInfo}
                  theme={theme}
                />
              ))}

              <CustomPagination
                page={page}
                rowsPerPage={limit}
                count={totalCount}
                onPageChange={handlePageChange}
              />
            </Box>
          ) : (

            /* -------- Desktop -------- */
            <Box
              sx={{
                ...rest?.sx,
                width: '100%',
                borderRadius: 2,
                bgcolor: '#FFFFFF',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  minHeight: 0,
                  flex: 1,
                  overflow: 'auto',
                  '& .MuiTableCell-stickyHeader': {
                    backgroundColor: '#F9FAFB',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    zIndex: 2
                  },
                  '& .MuiTableHead-root .MuiTableCell-root:last-of-type': {
                    position: 'sticky',
                    right: 0,
                    zIndex: 3,
                    backgroundColor: '#F9FAFB',
                    boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <Box sx={{ minWidth: { xs: 760, sm: 900, md: 1050 } }}>
                  <Table stickyHeader>

                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      headCells={dispenserTableHeaders}
                      onRequestSort={handleRequestSort}
                      sx={{
                        backgroundColor: '#F9FAFB',
                        borderBottom: `1px solid ${theme.palette.divider}`
                      }}
                    />

                    <TableBody>
                      {displayData.length > 0 ? (
                        displayData.map((dispenser) => {
                          const lastSyncedText = formatLastSynced(
                            dispenser.last_synced || dispenser.lastSynced
                          );

                          return (
                            <TableRow
                              hover
                              key={dispenser.id}
                              sx={{
                                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                '&:hover': {
                                  backgroundColor: '#F9FAFB'
                                }
                              }}
                            >
                              <TableCell align={dispenserAlignById.station_name}>
                                <Typography fontWeight={600}>
                                  {dispenser.station_name || 'N/A'}
                                </Typography>
                              </TableCell>

                              <TableCell align={dispenserAlignById.address}>
                                {dispenser.address || '-'}
                              </TableCell>

                              <TableCell align={dispenserAlignById.site_name}>
                                {dispenser.site_name || '-'}
                              </TableCell>

                              <TableCell align={dispenserAlignById.tank_name}>
                                {getTankName(dispenser)}
                              </TableCell>

                              <TableCell align={dispenserAlignById.mode}>
                                {dispenser.mode || '-'}
                              </TableCell>

                              <TableCell align={dispenserAlignById.article}>
                                {dispenser.article || '-'}
                              </TableCell>

                              <TableCell align={dispenserAlignById.status}>
                                <Stack spacing={0.4} alignItems="center">
                                  <StatusPill status={dispenser.status} />
                                  {lastSyncedText && (
                                    <Typography variant="caption" color="text.secondary">
                                      Last synced:
                                      {' '}
                                      {lastSyncedText}
                                    </Typography>
                                  )}
                                </Stack>
                              </TableCell>

                              <TableCell
                                align={dispenserAlignById.action}
                                sx={{
                                  position: 'sticky',
                                  right: 0,
                                  zIndex: 1,
                                  backgroundColor: '#FFFFFF',
                                  boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                                }}
                              >
                                <Tooltip title="View Details">
                                  <IconButton
                                    onClick={() => viewDispenserInfo(dispenser.id)}
                                    size="medium"
                                    aria-label="View details"
                                    sx={getTableActionIconButtonSx(theme)}
                                  >
                                    <ChevronRightIcon sx={TABLE_ACTION_ICON_SX} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={dispenserTableHeaders.length} align="center" sx={{ py: 6 }}>
                            <Typography color="text.secondary">
                              No Dispensers Found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>

                  </Table>
                </Box>
              </Box>

              <Divider sx={{ flexShrink: 0 }} />

              <Box sx={{ p: 1, flexShrink: 0, bgcolor: '#FFFFFF' }}>
                <CustomPagination
                  page={page}
                  rowsPerPage={limit}
                  count={totalCount}
                  onPageChange={handlePageChange}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

DispenserListTable.propTypes = {
  refreshData: PropTypes.bool,
  searchText: PropTypes.string,
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default DispenserListTable;
